import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useUrlFilters } from "@/lib/urlfilters";
import { addDays, fmtDT, todayStr } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export default function Attendance() {
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  // The dashboard's LATE TODAY tile lands here as ?from=<today>&to=<today>&only=late.
  const [f, setF] = useUrlFilters({ from: "", to: "", only: "all", q: "" });
  const [from, setFrom] = useState(f.from || addDays(todayStr(), -7));
  const [to, setTo] = useState(f.to || todayStr());
  const [siteFilter, setSiteFilter] = useState("all");
  const [correctFor, setCorrectFor] = useState(null);
  const [cForm, setCForm] = useState({ event_type: "check_in", corrected_ts: "", reason: "" });
  const [showUnfilled, setShowUnfilled] = useState(false);

  const load = () => {
    api.get(`/attendance?from_date=${from}&to_date=${to}${siteFilter !== "all" ? `&site_id=${siteFilter}` : ""}`)
      .then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [from, to, siteFilter]);
  useEffect(() => { api.get("/sites").then((r) => setSites(r.data)).catch(() => {}); }, []);

  const approve = async (row, approved) => {
    try {
      await api.post("/attendance/approve", { shift_id: row.shift_id, approved });
      toast.success(approved ? "Attendance approved — feeds payroll & billing" : "Approval revoked");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitCorrection = async () => {
    try {
      await api.post("/attendance/correct", { shift_id: correctFor.shift_id, ...cForm });
      toast.success("Correction recorded — original event preserved");
      setCorrectFor(null);
      setCForm({ event_type: "check_in", corrected_ts: "", reason: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const unfilled = rows.filter((r) => !r.guard_id).length;
  // ⚠️ LATE is decided by the SERVER's grace period, which the row carries as
  // `late_grace_minutes` -- not by a threshold re-guessed here. The dashboard
  // tile and this list must agree, and they only can if one number decides it.
  const isLate = (r) => r.late_minutes != null
    && r.late_minutes > (r.late_grace_minutes ?? 15);
  const base = showUnfilled ? rows : rows.filter((r) => r.guard_id);
  const late = f.only === "late" ? base.filter(isLate) : base;
  // ⚠️ Searches the GUARD, not the whole row. Matching every field would let
  // "Main" find every shift at Main Entrance and bury the person being looked
  // for — and the reason anybody types a name here is that they are chasing one
  // guard's attendance, not a site's.
  //
  // Accent- and case-insensitive, and it matches on any part of the name, so
  // "delacruz", "Ramon" and "cruz" all find Ramon Delacruz.
  const norm = (x) => String(x || "").toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "");
  const needle = norm(f.q).trim();
  const shown = !needle ? late
    : late.filter((r) => norm(r.guard_name).includes(needle)
      || norm(r.ref_no).includes(needle));

  return (
    <div data-testid="attendance-page">
      <PageHeader title="Attendance Review" sub="Scheduled vs actual · corrections preserve the original event" />
      <div className="flex flex-wrap gap-2 mb-3 items-end">
        <div><Label className="text-[10px] uppercase font-bold">From</Label><Input data-testid="att-from" type="date" className="rounded-none" value={from} onChange={(e) => { setFrom(e.target.value); setF({ from: e.target.value }); }} /></div>
        <div><Label className="text-[10px] uppercase font-bold">To</Label><Input data-testid="att-to" type="date" className="rounded-none" value={to} onChange={(e) => { setTo(e.target.value); setF({ to: e.target.value }); }} /></div>
        <div className="w-56">
          <Label className="text-[10px] uppercase font-bold">Site</Label>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger data-testid="att-site-filter" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All sites</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {/*
          ⚠️ An UNFILLED post has no attendance to review. Twenty-one of them
          sat in this table on one day, each offering "Correct" and "Approve" on
          a shift nobody worked — the two rows that needed a supervisor were
          buried among them. An empty post is a scheduling problem, so it is
          counted here and worked on the Schedule.
        */}
        <div className="w-60">
          <Label className="text-[10px] uppercase font-bold">Guard</Label>
          <Input data-testid="att-search" className="rounded-none mt-1" placeholder="Search by name…"
                 value={f.q} onChange={(e) => setF({ q: e.target.value })} />
        </div>
        <Button variant="outline" className="rounded-none h-10" data-testid="att-toggle-late"
                onClick={() => setF({ only: f.only === "late" ? "all" : "late" })}>
          {f.only === "late" ? "Show everybody" : "Only late arrivals"}
        </Button>
        <Button variant="outline" className="rounded-none h-10" data-testid="att-toggle-unfilled"
                onClick={() => setShowUnfilled((v) => !v)}>
          {showUnfilled ? "Hide unfilled posts" : `Show unfilled posts${unfilled ? ` (${unfilled})` : ""}`}
        </Button>
      </div>

      {unfilled > 0 && !showUnfilled && (
        <div className="border border-border p-3 mb-3 text-xs text-muted-foreground" data-testid="att-unfilled-note">
          <span className="text-foreground font-semibold">{unfilled} post(s) in this range have no guard
          assigned</span>, so there is no attendance to review on them. They are hidden here and filled on
          the <Link to="/schedule" className="underline">Schedule</Link>.
        </div>
      )}

      {needle && (
        <div className="border border-border p-3 mb-3 text-xs text-muted-foreground flex flex-wrap items-center gap-2"
             data-testid="att-search-banner">
          <span>
            <span className="font-mono text-foreground">{shown.length}</span> row(s) matching{" "}
            <span className="font-mono text-foreground">{f.q}</span>
            {" "}in {from} → {to}. A guard with no rows here worked no shift in this range —
            widen the dates before concluding they were absent.
          </span>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                  data-testid="att-search-clear" onClick={() => setF({ q: "" })}>
            Clear
          </Button>
        </div>
      )}

      <DataTable testid="attendance-table" rows={shown} empty="No attendance in this range"
        columns={[
          { key: "date", label: "Date", render: (r) => <div><div className="font-mono text-xs font-bold">{r.date}</div><StatusBadge value={r.shift_type} /></div> },
          { key: "guard", label: "Guard", render: (r) => <div><div className="font-semibold">{r.guard_name}</div><div className="text-xs text-muted-foreground">{r.site_name}{r.post_name ? ` · ${r.post_name}` : ""}</div></div> },
          { key: "scheduled", label: "Scheduled", render: (r) => <div className="font-mono text-xs">{fmtDT(r.scheduled_in)}<br />{fmtDT(r.scheduled_out)}</div> },
          { key: "actual", label: "Actual", render: (r) => <div className="font-mono text-xs">{fmtDT(r.actual_in)}{r.corrected ? " *" : ""}<br />{fmtDT(r.actual_out)}{r.corrected ? " *" : ""}</div> },
          { key: "variance", label: "Late / Early", render: (r) => <span className="font-mono text-xs">{r.late_minutes !== null && r.late_minutes !== undefined ? `${r.late_minutes}m` : "—"} / {r.early_minutes !== null && r.early_minutes !== undefined ? `${r.early_minutes}m` : "—"}</span> },
          { key: "geo", label: "Geofence", render: (r) => <div>{r.in_geo ? <StatusBadge value={r.in_geo} /> : "—"}{r.in_distance_m !== null && r.in_distance_m !== undefined && <div className="font-mono text-[10px] text-muted-foreground">{r.in_distance_m}m</div>}</div> },
          { key: "row_status", label: "Status", render: (r) => <div><StatusBadge value={r.row_status} />{r.approved && <div className="font-mono text-[9px] text-[hsl(var(--success))] mt-1">APPROVED</div>}</div> },
          {
            key: "actions", label: "Actions", render: (r) => (
              <div className="flex flex-wrap gap-1">
                {/*
                  A post with no guard has nothing to correct and nobody to pay,
                  so the buttons are not offered. Guarding the write and leaving
                  the button live only earns somebody a refusal for pressing it.
                */}
                {!r.guard_id ? (
                  <span className="text-[11px] text-muted-foreground">nobody was posted</span>
                ) : (
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" data-testid={`correct-${r.ref_no}`} onClick={() => { setCorrectFor(r); setCForm({ event_type: "check_in", corrected_ts: "", reason: "" }); }}>Correct</Button>
                )}
                {r.guard_id && (!r.approved
                  ? <Button size="sm" className="rounded-none h-7 text-xs" data-testid={`approve-${r.ref_no}`} onClick={() => approve(r, true)}>Approve</Button>
                  : <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" data-testid={`unapprove-${r.ref_no}`} onClick={() => approve(r, false)}>Revoke</Button>)}
              </div>
            ),
          },
        ]} />
      <p className="font-mono text-[10px] text-muted-foreground mt-2">* corrected value shown; original device event is preserved and audited.</p>

      <Dialog open={!!correctFor} onOpenChange={() => setCorrectFor(null)}>
        <DialogContent className="rounded-none" data-testid="correct-dialog">
          <DialogHeader><DialogTitle className="font-heading">Correct — {correctFor?.guard_name} · {correctFor?.date}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Event</Label>
              <Select value={cForm.event_type} onValueChange={(v) => setCForm({ ...cForm, event_type: v })}>
                <SelectTrigger data-testid="correct-event-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="check_in">Check-in</SelectItem><SelectItem value="check_out">Check-out</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Corrected timestamp</Label><Input data-testid="correct-ts" type="datetime-local" className="rounded-none mt-1" value={cForm.corrected_ts} onChange={(e) => setCForm({ ...cForm, corrected_ts: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Reason (required, audited)</Label><Textarea data-testid="correct-reason" className="rounded-none mt-1" value={cForm.reason} onChange={(e) => setCForm({ ...cForm, reason: e.target.value })} /></div>
            <Button data-testid="correct-submit" className="w-full rounded-none" onClick={submitCorrection} disabled={!cForm.corrected_ts || !cForm.reason.trim()}>Record correction</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
